<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Agreement,
  CollateralType,
  Data,
  SettlementType,
  collateralTypes,
  settlementTypes,
} from '@/connect';
import { useAgreementsStore } from '@/store/agreements-store';
import { useCompaniesStore } from '@/store/companies-store';

const props = withDefaults(
  defineProps<{
    agreementIds?: string[];
    emptyValue?: string;
  }>(),
  {
    agreementIds: () => [],
    emptyValue: 'Any',
  }
);

const agreementsStore = useAgreementsStore();
const companiesStore = useCompaniesStore();

const isMenuOpen = ref(false);

const agreements = computed(() => {
  return props.agreementIds
    .map((id) => agreementsStore.getAgreement(id))
    .filter((agreement): agreement is Data<Agreement> => !!agreement);
});

const label = computed(() => {
  const count = agreements.value.length;
  return count === 1 ? '1 agreement' : `${count} agreements`;
});

function getCounterpartyName(counterpartyId: string): string {
  const company = companiesStore.getCompany(counterpartyId);
  return company?.companyName ?? 'Unknown';
}

function getSettlementLabel(type: SettlementType): string {
  return settlementTypes.get(type) ?? 'Unknown';
}

function getCollateralLabel(type: CollateralType): string {
  return collateralTypes.get(type) ?? 'Unknown';
}
</script>

<template>
  <v-menu
    v-if="agreements.length > 0"
    v-model="isMenuOpen"
    :close-on-content-click="false"
    max-width="700"
    offset-y
  >
    <template #activator="{ on, attrs }">
      <div
        v-bind="attrs"
        class="agreement-popover-activator d-flex align-center justify-end gap-1 text-decoration-none"
        data-test-id="agreement-popover-activator"
        v-on="on"
      >
        {{ label }} <v-icon class="ml-1" small>mdi-format-list-bulleted</v-icon>
      </div>
    </template>
    <v-card>
      <v-card-title
        class="agreement-popover-header d-flex justify-space-between align-center py-2 body-1"
      >
        <span>{{ label }}</span>
        <v-btn icon small @click="isMenuOpen = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <div class="agreement-table-wrapper">
        <v-simple-table data-test-id="agreement-popover-table" dense>
          <colgroup>
            <col style="width: 180px" />
            <col style="width: 180px" />
            <col style="width: 120px" />
            <col style="width: 140px" />
          </colgroup>
          <tbody>
            <tr
              v-for="(agreement, index) in agreements"
              :key="agreement.id"
              :style="{ backgroundColor: index % 2 === 0 ? '#333' : 'transparent' }"
            >
              <td>{{ agreement.shortName }}</td>
              <td>{{ getCounterpartyName(agreement.counterpartyId) }}</td>
              <td>{{ getSettlementLabel(agreement.settlementType) }}</td>
              <td>{{ getCollateralLabel(agreement.collateralType) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </v-card>
  </v-menu>
  <span v-else>{{ emptyValue }}</span>
</template>

<style lang="scss" scoped>
.agreement-popover-activator {
  cursor: pointer;
  text-decoration: underline;
  color: var(--v-primary-base);
  background-color: #444;
  padding: 3px 6px;
  line-height: 1;
  border-radius: 4px;
  width: 8rem;
}

.agreement-popover-header {
  background-color: #1a1a1a;
}

.agreement-table-wrapper {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;

  ::v-deep td {
    white-space: nowrap;
    padding: 0 12px !important;
  }
}
</style>
